import { Box, CircularProgress, Typography } from "@outschool/backpack";
import { SortOrder } from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import {
  AddClassPostCommentInput,
  useValidMentions,
} from "@outschool/ui-components-classroom";
import { ErrorMessage } from "@outschool/ui-components-shared";
import React, { useEffect, useRef, useState } from "react";

import * as Env from "../../Env";
import { LearnerSinglePostQuery } from "../../generated/graphql";
import { useLearnerClassroomAddClassPostCommentMutation } from "../../hooks/useLearnerClassroomAddClassPostCommentMutation";
import { useLearnerDeleteClassPostCommentMutation } from "../../hooks/useLearnerDeleteClassPostCommentMutation";
import { useCurrentLearner } from "../../providers/CurrentLearnerProvider";
import {
  updateLearnerClassroomSinglePostsCache,
  useSinglePostQuery,
} from "../../queries/LearnerSinglePostQuery";
import { ClassDiscussionComment } from "../ClassDiscussion";
import { useMessageTeacherDrawerContext } from "./MessageTeacherDrawerContext";
import ThreadViewLayout from "./ThreadViewLayout";

type CommentSubmitInput = Parameters<
  React.ComponentProps<typeof AddClassPostCommentInput>["onSubmit"]
>[0];

interface PublicThreadDetailViewProps {
  classPostUid: string;
  postTitle: string | null;
  onBack: () => void;
  onClose: () => void;
  onCommentsChanged: () => void;
}

export default function PublicThreadDetailView({
  classPostUid,
  postTitle,
  onBack,
  onClose,
  onCommentsChanged,
}: PublicThreadDetailViewProps) {
  const { t } = useTranslation(
    "learnerApp\\MessageTeacherButton\\PublicThreadDetailView",
  );
  const { section, leader } = useMessageTeacherDrawerContext();
  const currentLearner = useCurrentLearner()!;
  const [error, setError] = useState<unknown>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data, loading } = useSinglePostQuery({
    sectionUid: section.uid,
    classPostUid,
    creationDateSortOrder: SortOrder.Ascending,
  });
  const classPost = data?.classroom?.postByUid ?? null;
  const comments = classPost?.comments ?? [];
  const usersCanComment = data?.classroom?.usersCanComment === true;

  const addComment = useLearnerClassroomAddClassPostCommentMutation({
    updateClassroomCache: updateLearnerClassroomSinglePostsCache,
  });
  const deleteComment = useLearnerDeleteClassPostCommentMutation();

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
  }, [comments.length]);

  const trackingMetadata = {
    userUid: currentLearner.parent?.uid,
    learnerUid: currentLearner.uid,
    postUid: classPostUid,
  };

  const handleCommentSubmit = async ({
    text,
    attachments,
    video,
    mentions,
  }: CommentSubmitInput) => {
    setError(null);
    try {
      await addComment({
        classPostUid,
        text,
        attachments: attachments ?? undefined,
        video: video ?? undefined,
        learner: currentLearner,
        mentions: mentions ?? undefined,
      });
      onCommentsChanged();
    } catch (submitError) {
      setError(submitError);
      throw submitError;
    }
  };

  return (
    <ThreadViewLayout
      postTitle={classPost?.title ?? postTitle ?? t("Class discussion")}
      postSubtitle={t("Class discussion · Visible to everyone in class")}
      blockUid={classPost ? classPostUid : undefined}
      onBack={onBack}
      onClose={onClose}
    >
      <Box
        ref={scrollRef}
        data-test-id="PublicThreadDetailView"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 20,
          p: 16,
        }}
      >
        {loading && !classPost ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 16 }}>
            <CircularProgress size={24} />
          </Box>
        ) : !classPost ? (
          <Typography variant="body2" color="text.secondary">
            {t("This discussion is no longer available.")}
          </Typography>
        ) : (
          comments.map(comment => {
            const isSender = comment.sender.uid === currentLearner.uid;
            return (
              <ClassDiscussionComment
                key={comment.uid}
                comment={comment}
                isEducator={comment.sender.uid === leader?.uid}
                isSender={isSender}
                timeZone={currentLearner.timeZone}
                onDelete={
                  isSender
                    ? async () => {
                        await deleteComment({
                          classPostUid,
                          commentUid: comment.uid,
                        });
                        onCommentsChanged();
                      }
                    : undefined
                }
                trackingMetadata={{
                  commentUid: comment.uid,
                  ...trackingMetadata,
                }}
              />
            );
          })
        )}
      </Box>
      {classPost && usersCanComment && (
        <PublicThreadComposer
          classPost={classPost}
          mentionNames={data?.classroom?.mentionNames}
          error={error}
          onSubmit={handleCommentSubmit}
        />
      )}
    </ThreadViewLayout>
  );
}

type SinglePost = LearnerSinglePostQuery["classroom"];

function PublicThreadComposer({
  classPost,
  mentionNames,
  error,
  onSubmit,
}: {
  classPost: NonNullable<SinglePost["postByUid"]>;
  mentionNames: SinglePost["mentionNames"] | undefined;
  error: unknown;
  onSubmit: (input: CommentSubmitInput) => Promise<void>;
}) {
  const { t } = useTranslation(
    "learnerApp\\MessageTeacherButton\\PublicThreadDetailView",
  );
  const mentionableNames = useValidMentions(classPost, mentionNames);

  return (
    <Box sx={{ px: 16, pb: 16 }}>
      <ErrorMessage value={error} showStatusPageLink={Env.IS_READ_ONLY_MODE} />
      <AddClassPostCommentInput
        compact
        placeholder={t("Add to the discussion")}
        onSubmit={onSubmit}
        mentionNames={mentionableNames}
      />
    </Box>
  );
}
